import { create } from 'zustand';

// the store module only: the feature barrel also carries the flow's dialogs,
// and the sign-out one imports the logout routine.
import { useFlowStore } from '@/features/flow/store';
import { useTradingSessionStore } from '@/features/tradingSession/store';
import { readConnectedAddress } from '@/features/wallet/connectedAccount';

import {
    DEPOSIT_WALLET_REQUIREMENT,
    WITHDRAW_WALLET_REQUIREMENT,
} from './custodialWalletRequirements';

type PanelTab = 'deposit' | 'withdraw';

interface CustodialPanelState {
    isOpen: boolean;
    defaultTab: PanelTab;
    prefillAmount: number | null;
    /**
     * The panel opened by itself to show a deposit or withdrawal already in
     * flight (not by the user asking to start one). Watching a job needs no
     * wallet, so such an open bypasses the wallet requirement.
     */
    openedForJob: boolean;
    /**
     * Set when opening the panel was diverted to the Connected Wallet window it
     * requires (a passkey or social session has no owner wallet). Once that
     * window mints a wallet authority the panel opens on this tab; cleared
     * unconditionally when the window resolves either way.
     */
    reopenAfterWalletAuthority: {
        tab: PanelTab;
        prefillAmount: number | null;
    } | null;
    openDeposit: (prefillAmount?: number) => void;
    openWithdraw: () => void;
    /** The auto-open for an in-flight job; see `openedForJob`. Never gated. */
    openForJob: (kind: PanelTab) => void;
    /**
     * The reopen after a diverted open's wallet window resolved. Not gated:
     * the ceremony that just completed connected the wallet and minted the
     * eip712 window, and re-gating here could bounce the user back into the
     * window they just finished (or cancelled) forever.
     */
    reopenDiverted: (tab: PanelTab, prefillAmount: number | null) => void;
    close: () => void;
    clearReopenAfterWalletAuthority: () => void;
}

/**
 * A wallet (eip712) trading window PLUS a connected wallet are what deposits
 * and withdrawals need: a passkey or social session cannot send the Cronos
 * transaction a deposit needs, and an eip712 window whose wallet has since
 * disconnected cannot either (the panel would show a $0 wallet with nothing
 * to do). Both are read synchronously when the user asks to open a tab, so
 * the decision never races a status refresh; wagmi reports the persisted
 * address while it reconnects, so a reload does not misread as disconnected.
 * The V1 to V2 migration reads the same gate (`features/migration`).
 */
export function hasUsableWalletAuthority(): boolean {
    const session = useTradingSessionStore.getState();
    return (
        session.status?.state === 'active' &&
        session.status.method === 'eip712' &&
        session.hasActiveAuthority() &&
        readConnectedAddress() !== undefined
    );
}

/**
 * The browser wallet flow (V2) signs its own deposits and withdrawals with the
 * key held here, so none of the above applies: its funds panel renders from
 * this same store, which is what keeps every existing "open deposit" entry
 * (header, rail, insufficient-balance toast) working in both flows.
 */
function isBrowserWalletFlow(): boolean {
    return useFlowStore.getState().active === 'v2';
}

export const useCustodialPanelState = create<CustodialPanelState>((set) => ({
    isOpen: false,
    defaultTab: 'deposit',
    prefillAmount: null,
    openedForJob: false,
    reopenAfterWalletAuthority: null,
    openDeposit: (amount) => {
        const prefillAmount = amount ?? null;
        if (!isBrowserWalletFlow() && !hasUsableWalletAuthority()) {
            set({
                reopenAfterWalletAuthority: { tab: 'deposit', prefillAmount },
            });
            useTradingSessionStore
                .getState()
                .requestConnectedWalletWindow(DEPOSIT_WALLET_REQUIREMENT);
            return;
        }
        set({
            isOpen: true,
            defaultTab: 'deposit',
            prefillAmount,
            openedForJob: false,
        });
    },
    openWithdraw: () => {
        if (!isBrowserWalletFlow() && !hasUsableWalletAuthority()) {
            set({
                reopenAfterWalletAuthority: {
                    tab: 'withdraw',
                    prefillAmount: null,
                },
            });
            useTradingSessionStore
                .getState()
                .requestConnectedWalletWindow(WITHDRAW_WALLET_REQUIREMENT);
            return;
        }
        set({
            isOpen: true,
            defaultTab: 'withdraw',
            prefillAmount: null,
            openedForJob: false,
        });
    },
    openForJob: (kind) =>
        set({
            isOpen: true,
            defaultTab: kind,
            prefillAmount: null,
            openedForJob: true,
        }),
    reopenDiverted: (tab, prefillAmount) =>
        set({
            isOpen: true,
            defaultTab: tab,
            prefillAmount,
            openedForJob: false,
        }),
    close: () =>
        set({ isOpen: false, prefillAmount: null, openedForJob: false }),
    clearReopenAfterWalletAuthority: () =>
        set({ reopenAfterWalletAuthority: null }),
}));
